import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { bomberosActividadesAPI } from '../../lib/api';
import { useBomberosPermisos } from '../../hooks/useBomberosPermisos';
import { BomberosHeader } from '../../components/BomberosHeader';
import { ModalValidacion } from '../../components/bomberos/ModalValidacion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import {
    ArrowLeft,
    Edit,
    Trash2,
    CalendarDays,
    Clock,
    MapPin,
    Building2,
    User,
    Users,
    Truck,
    FileCheck,
    CheckCircle,
    XCircle,
    MessageSquare,
    AlertCircle,
} from 'lucide-react';

export default function ActividadDetallePage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { tienePermiso, esAdmin, loading: loadingPermisos } = useBomberosPermisos();

    const [loading, setLoading] = useState(true);
    const [actividad, setActividad] = useState(null);
    const [showValidacion, setShowValidacion] = useState(false);

    const puedeValidar = tienePermiso('validar_actividades') || esAdmin;

    useEffect(() => {
        loadActividad();
    }, [id]);

    const loadActividad = async () => {
        try {
            setLoading(true);
            const res = await bomberosActividadesAPI.getById(id);
            setActividad(res.data);
        } catch (err) {
            console.error('Error loading actividad:', err);
            toast.error('Error al cargar la actividad');
            navigate('/bomberos/actividades');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('¿Está seguro de eliminar esta actividad?')) return;
        
        try {
            await bomberosActividadesAPI.delete(id);
            toast.success('Actividad eliminada');
            navigate('/bomberos/actividades');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al eliminar');
        }
    };

    const getEstadoBadge = (estado) => {
        switch (estado) {
            case 'autorizado':
                return <Badge className="bg-green-500 hover:bg-green-600 text-white"><CheckCircle className="h-4 w-4 mr-1" />Autorizado</Badge>;
            case 'rechazado':
                return <Badge variant="destructive"><XCircle className="h-4 w-4 mr-1" />Rechazado</Badge>;
            default:
                return <Badge variant="secondary"><Clock className="h-4 w-4 mr-1" />Pendiente de Validación</Badge>;
        }
    };

    if (loading || loadingPermisos) {
        return (
            <>
                <BomberosHeader />
                <div className="space-y-6 p-6 pt-20 max-w-4xl mx-auto">
                    <Skeleton className="h-10 w-64" />
                    <Skeleton className="h-96" />
                </div>
            </>
        );
    }

    if (!actividad) {
        return (
            <>
                <BomberosHeader />
                <div className="flex flex-col items-center justify-center min-h-[50vh] pt-20 md:pt-20">
                    <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-lg text-muted-foreground">Actividad no encontrada</p>
                    <Button variant="outline" onClick={() => navigate('/bomberos/actividades')} className="mt-4">
                        Volver al listado
                    </Button>
                </div>
            </>
        );
    }

    return (
        <>
            <BomberosHeader />
            <div className="space-y-6 p-6 pt-20 max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/actividades')} data-testid="back-btn">
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div>
                            <h1 className="text-2xl font-bold flex items-center gap-2">
                                <CalendarDays className="h-6 w-6 text-orange-500" />
                                Detalle de Actividad
                            </h1>
                            <p className="text-muted-foreground">
                                Información completa de la solicitud
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {actividad.estado === 'pendiente' && (
                            <>
                                {puedeValidar && (
                                    <Button onClick={() => setShowValidacion(true)} data-testid="validar-btn">
                                        <FileCheck className="h-4 w-4 mr-2" />
                                        Validar
                                    </Button>
                                )}
                                <Button variant="outline" onClick={() => navigate(`/bomberos/actividades/${id}/editar`)} data-testid="edit-btn">
                                    <Edit className="h-4 w-4 mr-2" />
                                    Editar
                                </Button>
                                <Button variant="destructive" onClick={handleDelete} data-testid="delete-btn">
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Eliminar
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                {/* Estado prominente */}
                <div className="flex justify-center">
                    {getEstadoBadge(actividad.estado)}
                </div>

                {/* Info Principal */}
                <Card>
                    <CardHeader>
                        <CardTitle>Información de la Actividad</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Descripción */}
                        <div>
                            <h3 className="font-semibold text-lg">{actividad.actividad}</h3>
                        </div>

                        <Separator />

                        {/* Fecha, Hora, Lugar */}
                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="flex items-start gap-3">
                                <CalendarDays className="h-5 w-5 text-muted-foreground mt-0.5" />
                                <div>
                                    <p className="text-sm text-muted-foreground">Fecha</p>
                                    <p className="font-medium">
                                        {new Date(actividad.fecha).toLocaleDateString('es-CL', {
                                            weekday: 'long',
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}
                                    </p>
                                </div>
                            </div>
                            
                            <div className="flex items-start gap-3">
                                <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                                <div>
                                    <p className="text-sm text-muted-foreground">Horario</p>
                                    <p className="font-medium">
                                        {actividad.hora_inicio || '--:--'} - {actividad.hora_fin || '--:--'}
                                    </p>
                                </div>
                            </div>
                            
                            <div className="flex items-start gap-3">
                                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                                <div>
                                    <p className="text-sm text-muted-foreground">Lugar</p>
                                    <p className="font-medium">{actividad.lugar || 'No especificado'}</p>
                                </div>
                            </div>
                        </div>

                        <Separator />

                        {/* Compañía y OBAC */}
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="flex items-start gap-3">
                                <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
                                <div>
                                    <p className="text-sm text-muted-foreground">Compañía</p>
                                    <p className="font-medium">{actividad.compania_nombre || 'No asignada'}</p>
                                </div>
                            </div>
                            
                            <div className="flex items-start gap-3">
                                <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                                <div>
                                    <p className="text-sm text-muted-foreground">OBAC (Responsable)</p>
                                    <p className="font-medium">{actividad.obac_nombre || 'No asignado'}</p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Material Mayor */}
                {(actividad.material_mayor_detalles?.length > 0 || actividad.material_mayor_ids?.length > 0) && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Truck className="h-5 w-5" />
                                Material Mayor
                            </CardTitle>
                            <CardDescription>
                                Vehículos asignados a la actividad
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-wrap gap-2">
                                {actividad.material_mayor_detalles?.map((v, i) => (
                                    <Badge key={i} variant="outline" className="py-2">
                                        <Truck className="h-3 w-3 mr-1" />
                                        {v.codigo} - {v.tipo}
                                    </Badge>
                                ))}
                                {!actividad.material_mayor_detalles?.length && (
                                    <p className="text-muted-foreground text-sm">
                                        {actividad.material_mayor_ids?.length || 0} vehículo(s) asignado(s)
                                    </p>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Asistentes */}
                {(actividad.asistentes_detalles?.length > 0 || actividad.asistentes_ids?.length > 0) && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Users className="h-5 w-5" />
                                Asistentes
                            </CardTitle>
                            <CardDescription>
                                Personal asignado a la actividad
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-wrap gap-2">
                                {actividad.asistentes_detalles?.map((a, i) => (
                                    <Badge key={i} variant="secondary" className="py-2">
                                        <User className="h-3 w-3 mr-1" />
                                        {a.nombre}
                                    </Badge>
                                ))}
                                {!actividad.asistentes_detalles?.length && (
                                    <p className="text-muted-foreground text-sm">
                                        {actividad.asistentes_ids?.length || 0} asistente(s)
                                    </p>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Observaciones */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <MessageSquare className="h-5 w-5" />
                            Observaciones
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {actividad.observaciones_solicitante && (
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Del Solicitante:</p>
                                <p className="bg-muted/50 p-3 rounded-md">{actividad.observaciones_solicitante}</p>
                            </div>
                        )}
                        
                        {actividad.observacion_comandante && (
                            <div>
                                <p className="text-sm text-muted-foreground mb-1">Del Comandante:</p>
                                <p className={`p-3 rounded-md ${
                                    actividad.estado === 'autorizado' ? 'bg-green-50 dark:bg-green-950/30' :
                                    actividad.estado === 'rechazado' ? 'bg-red-50 dark:bg-red-950/30' :
                                    'bg-muted/50'
                                }`}>
                                    {actividad.observacion_comandante}
                                </p>
                            </div>
                        )}
                        
                        {!actividad.observaciones_solicitante && !actividad.observacion_comandante && (
                            <p className="text-muted-foreground text-sm">Sin observaciones</p>
                        )}
                    </CardContent>
                </Card>

                {/* Info Adicional */}
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
                            <div>
                                <span className="font-medium">Solicitante:</span> {actividad.solicitante_nombre || 'Desconocido'}
                            </div>
                            <div>
                                <span className="font-medium">Creado:</span> {new Date(actividad.created_at).toLocaleString('es-CL')}
                            </div>
                            {actividad.fecha_validacion && (
                                <div>
                                    <span className="font-medium">Validado:</span> {new Date(actividad.fecha_validacion).toLocaleString('es-CL')}
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Modal de Validación */}
            <ModalValidacion
                open={showValidacion}
                onOpenChange={setShowValidacion}
                item={actividad}
                itemType="actividad"
                onValidar={(data) => bomberosActividadesAPI.validar(id, data)}
                onSuccess={loadActividad}
            />
        </>
    );
}
